import clsx from "clsx";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faShieldHalved,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { panelActionCoreClass, panelActionHeightClass } from "./panelActionStyles";

export interface InvestigateSplitButtonProps {
  label: string;
  activeLabel?: string;
  disabled?: boolean;
  progressPercent?: number;
  onInvestigate: (userFocus?: string) => void;
  testIdPrefix?: string;
}

export function InvestigateSplitButton({
  label,
  activeLabel = "Investigating…",
  disabled = false,
  progressPercent = 0,
  onInvestigate,
  testIdPrefix = "investigate",
}: InvestigateSplitButtonProps) {
  const [steeringOpen, setSteeringOpen] = useState(false);
  const [focusText, setFocusText] = useState("");
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const investigating = disabled;
  const displayLabel = investigating ? activeLabel : label;

  const closeSteering = useCallback(() => {
    setSteeringOpen(false);
  }, []);

  const submitSteering = useCallback(() => {
    const focus = focusText.trim();
    onInvestigate(focus || undefined);
    setFocusText("");
    closeSteering();
  }, [closeSteering, focusText, onInvestigate]);

  useEffect(() => {
    if (!steeringOpen) return;
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [steeringOpen]);

  useEffect(() => {
    if (!steeringOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeSteering();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeSteering, steeringOpen]);

  return (
    <div className={clsx("relative inline-flex", panelActionHeightClass)} ref={menuRef}>
      {steeringOpen ? (
        <div
          className="absolute bottom-full left-0 z-20 mb-2 w-[32rem] max-w-[calc(100vw-3rem)] rounded-xl border border-white/10 bg-slate-800 p-3 shadow-lg"
          role="dialog"
          aria-label="Focus of the audit"
          data-testid={`${testIdPrefix}-steering-overlay`}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <label className="block text-xs text-slate-400" htmlFor={inputId}>
            Focus of the audit
          </label>
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            value={focusText}
            onChange={(event) => setFocusText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                submitSteering();
              }
            }}
            placeholder="e.g. Verify the second LOA contains the correct provider address"
            className="mt-1.5 w-full rounded-lg border border-white/10 bg-slate-700/80 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500"
            data-testid={`${testIdPrefix}-steering-input`}
          />
        </div>
      ) : null}

      <div
        className={clsx(
          "relative inline-flex h-full overflow-hidden rounded-lg",
          investigating ? "bg-indigo-600 opacity-95" : "bg-indigo-600"
        )}
      >
        {investigating ? (
          <span
            className="absolute inset-y-0 left-0 bg-indigo-400/35 transition-[width]"
            style={{ width: `${progressPercent}%` }}
          />
        ) : null}
        <button
          type="button"
          disabled={investigating}
          onClick={() => onInvestigate()}
          className={clsx(
            "relative z-[1] h-full gap-2 px-3 text-white",
            panelActionCoreClass,
            !investigating && "hover:bg-indigo-500"
          )}
          data-testid={testIdPrefix}
        >
          {!investigating ? <FontAwesomeIcon icon={faShieldHalved} className="text-xs" /> : null}
          {displayLabel}
        </button>
        <button
          type="button"
          disabled={investigating}
          onClick={() => setSteeringOpen((open) => !open)}
          aria-expanded={steeringOpen}
          aria-haspopup="dialog"
          aria-label="Steer investigation"
          className={clsx(
            "relative z-[1] h-full border-l border-indigo-400/40 px-2.5 text-white",
            panelActionCoreClass,
            !investigating && "hover:bg-indigo-500",
            steeringOpen && !investigating && "bg-indigo-500"
          )}
          data-testid={`${testIdPrefix}-menu`}
        >
          <FontAwesomeIcon icon={faEllipsisVertical} className="text-xs" />
        </button>
      </div>
    </div>
  );
}
